/**
 * Send the browser to `/login` when the API says the session is gone.
 *
 * Since C-100 every `/api/v1` and `/tiles` request passes Caddy's
 * `forward_auth`, which answers 401 once the session cookie has expired (24 h
 * idle, C-103) or been invalidated (an `AUTH_SECRET` rotation). Without this,
 * a page left open would show "API 401" banners instead of asking the user to
 * sign in again — which, with Microsoft SSO, is usually a silent redirect.
 *
 * A 401 is not proof the session is gone, though: the API also answers 401
 * when the web and API tiers disagree on the signing key, or on dev's :3000
 * port, whose rewrites reach the API unsigned. Redirecting then would loop —
 * `/login` sends a signed-in user straight back, the page reloads, the call
 * 401s again. So this asks Auth.js first, and only navigates when there is
 * really no session; otherwise the error surfaces as an error.
 *
 * One check at a time per page load: a screen fires several queries at once,
 * and each 401 must not queue another session probe or navigation.
 */

let pending = false;

export function redirectToLoginOnce(): void {
  if (typeof window === "undefined" || pending) return;
  if (window.location.pathname.startsWith("/login")) return;
  pending = true;
  void (async () => {
    try {
      const res = await fetch("/api/auth/session", {
        credentials: "include",
        cache: "no-store",
      });
      const session: unknown = await res.json().catch(() => null);
      const signedIn =
        res.ok &&
        session !== null &&
        typeof session === "object" &&
        "user" in session &&
        Boolean((session as { user?: unknown }).user);
      if (!signedIn) {
        window.location.assign("/login");
        return;
      }
    } catch {
      // The probe itself failed (network): leave the page as it is.
    }
    pending = false;
  })();
}

/** Test hook: the guard is module state. */
export function resetLoginRedirectForTests(): void {
  pending = false;
}
